/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { ITag } from '@fluentui/react/lib/Pickers';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Pill, PillProps } from 'src/core/components/Pill';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TagContainer } from 'src/core/components/TagContainer';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type PillContainerProps = {
    /**
     * The pills that are already added.
     */
    pills: PillProps[];

    /**
     * A callback for when a new pill is added.
     */
    onAddNewPill?: (name: PillProps['name']) => void;

    /**
     * A callback for when a pill remove icon is clicked.
     */
    onRemovePill?: () => void;

    /**
     * A callback for when adding new pill icon is clicked.
     */
    onAddIconClick?: () => void;

    /**
     * A flag whether addition of pills
     * is allowed or not.
     */
    allowAddPills?: boolean;

    /**
     * A flag whether input pill is shown or not.
     */
    showInputPill?: boolean;

    /**
     * Boolean to determines whether to show the confirm icon or not.
     */
    showConfirmIcon?: boolean;

    /**
     * The menu items to display in a pill's
     * contextual menu.
     */
    inputPillMenuItems?: IContextualMenuItem[];

    /**
     * The text to be displayed in the search box
     */
    inputPillPlaceholder?: string;

    /**
     * Add pill button tooltip.
     */
    addPillButtonAriaLabel?: string;

    /**
     * The initial value for the input pill
     */
    inputPillValue?: string;

    /**
     * Setter for the pill input value
     */
    setInputPillValue?: (val: string) => void;

    /**
     * The items to display when the filtered items
     * have a length of zero.
     */
    inputPillMenuNoContent?: (query: string) => IContextualMenuItem[];

    /**
     * Rendering function to the add pill button.
     */
    onRenderAddPillButtonContent?: () => React.ReactNode;

    /*
     * Controls whether the add feature button is disabled
     */
    isAddPillButtonDisabled?: boolean;

    /**
     * Controls whther the delete button of the pill is disabled.
     */
    isDeletePillButtonDisabled?: boolean;

    /*
     * Controls whether the add feature button is disabled
     */
    isLuisToolTipHidden?: boolean;

    /*
     * The message to show when the add pill button is disabled
     */
    disabledButtonLuisToolTipMessage?: string;

    /**
     * Add pill button role
     */
    addPillButtonRole?: string;
};

const messages = defineMessages({
    inputPillPlaceholder: {
        id: 'core.pillContainer.inputPillPlaceholder',
        defaultMessage: 'Type here ...'
    },
    addPillAriaLabel: {
        id: 'core.pillContainer.addPillAriaLabel',
        defaultMessage: 'Add value'
    }
});

const containerTheme = getScopedTheme('PillContainer');

const addButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: {
        height: 28,
        border: 'none',
        backgroundColor: 'transparent',
        minWidth: 'unset',
        padding: containerTheme.padding,
        margin: '5px 5px 5px 0px'
    },
    icon: { fontSize: 'smaller' }
});

/**
 * Renders the pills that displays and controls the
 * selected items form the contextual menu item
 */
export const PillContainer = (props: PillContainerProps) => {
    const { localize } = useLocalization();
    const {
        pills,
        onAddNewPill,
        onRemovePill,
        showInputPill,
        onAddIconClick,
        showConfirmIcon,
        setInputPillValue,
        inputPillValue = '',
        isLuisToolTipHidden,
        allowAddPills = true,
        inputPillMenuItems = [],
        isAddPillButtonDisabled,
        isDeletePillButtonDisabled,
        addPillButtonRole = 'button',
        onRenderAddPillButtonContent,
        disabledButtonLuisToolTipMessage,
        addPillButtonAriaLabel = localize(messages.addPillAriaLabel),
        inputPillPlaceholder = localize(messages.inputPillPlaceholder),
        inputPillMenuNoContent = (_: string): IContextualMenuItem[] => []
    } = props;

    const addPillButtonTooltipIdRef = React.useRef(getId('addPillButtonTooltip'));

    /**
     * Signals the creation of a new pill value that
     * is not one of the menu items provided.
     */
    const onCreateNewPill = (newName: string) => {
        if (setInputPillValue) {
            setInputPillValue(newName);
        }
        onAddNewPill(newName);
    };

    const addPillButton = (buttonProps: IButtonProps) => (
        <DefaultButton
            tabIndex={0}
            aria-haspopup="true"
            role={addPillButtonRole}
            styles={addButtonStyles}
            onClick={onAddIconClick}
            disabled={buttonProps.disabled}
            iconProps={{ iconName: 'Add' }}
            ariaLabel={addPillButtonAriaLabel}
            aria-describedby={addPillButtonTooltipIdRef.current}
            onRenderChildren={() => onRenderAddPillButtonContent && <>{onRenderAddPillButtonContent()}</>}
        />
    );

    return (
        <TagContainer<ITag>>
            {pills.map(pillProps => (
                <Pill key={pillProps.pillKey} isDeleteButtonDisabled={isDeletePillButtonDisabled} {...pillProps} />
            ))}

            {allowAddPills && showInputPill && (
                <Pill
                    initialEditMode
                    pillKey={getId()}
                    index={pills.length}
                    name={inputPillValue}
                    onRemovePill={onRemovePill}
                    menuItems={inputPillMenuItems}
                    showConfirmIcon={showConfirmIcon}
                    onPillValueChange={onCreateNewPill}
                    pillInputTextPlaceholder={inputPillPlaceholder}
                    onNoExactQueryMatchContent={inputPillMenuNoContent}
                />
            )}

            {allowAddPills && !showInputPill && (
                <RBACTooltip
                    hidden={isLuisToolTipHidden}
                    id={addPillButtonTooltipIdRef.current}
                    content={isAddPillButtonDisabled && !isLuisToolTipHidden ? disabledButtonLuisToolTipMessage : addPillButtonAriaLabel}
                    displayedUserRole=""
                    componentSurroundedByToolTip={addPillButton({ disabled: isAddPillButtonDisabled })}
                />
            )}
        </TagContainer>
    );
};
